// src/components/Admin.js
import { useState } from "react";

function Admin({ addFeed }) {
  const [url, setUrl] = useState("");
  const [isValidating, setIsValidating] = useState(false);

  // Styles
  const styles = {
    container: {
      backgroundColor: "white",
      borderRadius: "8px",
      padding: "20px",
      marginBottom: "20px",
      boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
    },
    title: {
      fontSize: "18px",
      fontWeight: "600",
      color: "#2c3e50",
      marginTop: "0",
      marginBottom: "15px",
    },
    inputGroup: {
      display: "flex",
      gap: "10px",
      flexWrap: "wrap",
    },
    input: {
      flex: "1",
      minWidth: "250px",
      padding: "10px 12px",
      fontSize: "14px",
      border: "1px solid #ced4da",
      borderRadius: "4px",
      outline: "none",
      transition: "border-color 0.2s",
    },
    inputError: {
      borderColor: "#e74c3c",
    },
    button: {
      backgroundColor: "#2ecc71",
      color: "white",
      border: "none",
      padding: "10px 16px",
      borderRadius: "4px",
      cursor: "pointer",
      fontWeight: "500",
      fontSize: "14px",
      transition: "background-color 0.2s",
    },
    buttonDisabled: {
      backgroundColor: "#bdc3c7",
      cursor: "not-allowed",
    },
    errorMessage: {
      color: "#e74c3c",
      fontSize: "12px",
      marginTop: "5px",
    },
  };

  const validateUrl = (urlString) => {
    try {
      const url = new URL(urlString);
      return url.protocol === "http:" || url.protocol === "https:";
    } catch {
      return false;
    }
  };

  const handleAddFeed = async () => {
    if (!url.trim()) {
      alert("Please enter a URL");
      return;
    }

    if (!validateUrl(url)) {
      alert("Please enter a valid HTTP or HTTPS URL");
      return;
    }

    setIsValidating(true);

    try {
      // Test if the RSS feed can be fetched
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/rss/api/fetch-rss?url=${encodeURIComponent(url)}`
      );

      if (!response.ok) {
        throw new Error("Failed to fetch RSS feed. Please check the URL.");
      }

      const feedData = await response.json();
      
      if (!feedData || !Array.isArray(feedData) || feedData.length === 0) {
        throw new Error("No feed items found. Please verify this is a valid RSS feed.");
      }

      // If validation passes, add the feed
      addFeed(url);
      setUrl(""); // Clear the input field after adding the feed
      alert(`RSS feed added successfully! Found ${feedData.length} items.`);
    } catch (error) {
      console.error("Error validating RSS feed:", error);
      alert(`Error: ${error.message}`);
    } finally {
      setIsValidating(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !isValidating) {
      handleAddFeed();
    }
  };

  return (
    <div style={styles.container} className="rss-add-container">
      <style>{`
        @media (max-width: 1024px) {
          .rss-add-container { padding: 16px; }
        }
        @media (max-width: 768px) {
          .rss-add-container { padding: 14px; }
          .rss-add-inputs { flex-direction: column; }
          .rss-add-inputs input { min-width: 100%; }
          .rss-add-inputs button { width: 100%; }
        }
        @media (max-width: 480px) {
          .rss-add-container { padding: 12px; }
        }
      `}</style>
      <h2 style={styles.title}>Add RSS Feed</h2>
      <div style={styles.inputGroup} className="rss-add-inputs">
        <input
          type="text"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder="Enter RSS Feed URL (e.g., https://example.com/rss)"
          disabled={isValidating}
          style={{
            ...styles.input,
            ...(url && !validateUrl(url) ? styles.inputError : {}),
          }}
          onFocus={(e) => {
            e.currentTarget.style.borderColor = "#3498db";
          }}
          onBlur={(e) => {
            e.currentTarget.style.borderColor = url && !validateUrl(url) ? "#e74c3c" : "#ced4da";
          }}
        />
        <button
          onClick={handleAddFeed}
          disabled={isValidating || !url.trim()}
          style={{
            ...styles.button,
            ...(isValidating || !url.trim() ? styles.buttonDisabled : {}),
          }}
          onMouseOver={(e) => {
            if (!isValidating && url.trim()) {
              e.currentTarget.style.backgroundColor = "#27ae60";
            }
          }}
          onMouseOut={(e) => {
            if (!isValidating && url.trim()) {
              e.currentTarget.style.backgroundColor = "#2ecc71";
            }
          }}
        >
          {isValidating ? "Validating..." : "Add Feed"}
        </button>
      </div>
      {url && !validateUrl(url) && (
        <div style={styles.errorMessage}>
          Please enter a valid HTTP or HTTPS URL
        </div>
      )}
    </div>
  );
}

export default Admin;
